import { GetPriceResult } from '@/features/swap/logic/types'
import { TokenWithValue } from '@/domain/common/types'
import * as Collapsible from '@radix-ui/react-collapsible'
import { cn } from '@/ui/utils/style'
import { ChevronDownIcon, ExternalLinkIcon } from 'lucide-react'
import { BaseUnitNumber } from '@/domain/types/NumericValues'
import { getRouterName } from '@/config/consts'
import { useBlockExplorerAddressLink } from '@/domain/hooks/useBlockExplorerAddressLink'
import { usePageChainId } from '@/domain/hooks/usePageChainId'
import { useState } from 'react'
import { estimateUSDDifference } from '../logic/priceDataFormatters'
import { estimateTokenRatio } from '../logic/priceDataFormatters'

export function InfoDropdown({
  priceData,
  fromToken,
  toToken,
  priceUpdating,
}: { priceData: GetPriceResult; fromToken: TokenWithValue; toToken: TokenWithValue; priceUpdating: boolean }) {
  const [open, setOpen] = useState(false)
  const { chainId } = usePageChainId()
  const routerLink = useBlockExplorerAddressLink({ address: priceData.result.router, chainId })

  return (
    <Collapsible.Root
      open={open}
      onOpenChange={setOpen}
      className={cn('mt-2 text-xs', {
        'opacity-50': priceUpdating,
      })}
    >
      <Collapsible.Trigger asChild className="mb-2">
        <button className="flex w-full items-center justify-between text-white/50">
          <span>
            1 {fromToken.token.symbol} ={' '}
            {estimateTokenRatio({ priceData, fromToken: fromToken.token, toToken: toToken.token })}{' '}
            {toToken.token.symbol}
          </span>
          <ChevronDownIcon className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} />
        </button>
      </Collapsible.Trigger>
      <Collapsible.Content>
        <RowItem
          label="Summary"
          value={
            !priceData.result.inputAmountUSD || !priceData.result.outputAmountUSD
              ? 'Could not fetch price data'
              : `$${Number(priceData.result.inputAmountUSD).toFixed(2)} ${fromToken.token.symbol} -> $${Number(priceData.result.outputAmountUSD).toFixed(2)} ${toToken.token.symbol} (${estimateUSDDifference(priceData)})`
          }
        />
        <RowItem
          label="Min. received"
          value={`${toToken.token.fromBaseUnit(BaseUnitNumber(priceData.result.minOutputAmount))} ${toToken.token.symbol}`}
        />
        <RowItem
          label="Router"
          value={
            <a
              href={routerLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 hover:underline"
            >
              {getRouterName(priceData.result.router)} <ExternalLinkIcon className="h-3 w-3" />
            </a>
          }
        />
      </Collapsible.Content>
    </Collapsible.Root>
  )
}

function RowItem({ label, value }: { label: string; value: string | React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2 py-1">
      <p className="text-white/50">{label}</p>
      <p>{value}</p>
    </div>
  )
}
